import { useEffect, useState, type FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Activity } from 'lucide-react'
import { useAuth } from '../auth/AuthContext'
import { ApiError } from '../api/client'
import './LoginPage.css'

type LocationState = { from?: { pathname?: string } } | null

export function LoginPage() {
  const { login, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // If somehow we render this page while already authenticated (e.g. user
  // hits /login with an active session), bounce to the destination.
  useEffect(() => {
    if (isAuthenticated) {
      const dest = (location.state as LocationState)?.from?.pathname ?? '/'
      navigate(dest, { replace: true })
    }
  }, [isAuthenticated, location.state, navigate])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login(username, password)
      const dest = (location.state as LocationState)?.from?.pathname ?? '/'
      navigate(dest, { replace: true })
    } catch (err) {
      // Don't surface backend exception details; classify by failure kind
      // so a 502/unreachable API isn't mislabeled as a bad password.
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setError('Invalid username or password')
        } else {
          setError(`Server error (${err.status}) — the API may be unreachable. Try again.`)
        }
      } else {
        setError('Cannot reach the server — check your connection and try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={handleSubmit}>
        <div className="login-header">
          <Activity size={24} />
          <span className="login-title">Trading Platform</span>
        </div>

        <label className="login-field">
          <span className="login-label">Username</span>
          <input
            type="text"
            className="login-input"
            autoComplete="username"
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            disabled={submitting}
          />
        </label>

        <label className="login-field">
          <span className="login-label">Password</span>
          <input
            type="password"
            className="login-input"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={submitting}
          />
        </label>

        {error && <div className="login-error">{error}</div>}

        <button
          type="submit"
          className="btn btn-primary login-submit"
          disabled={submitting || !username || !password}
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  )
}
